import React, { useState, useMemo, useEffect } from "react";
import { LuPencil } from "react-icons/lu";
import { RiDeleteBinLine } from "react-icons/ri";
import { MdKeyboardArrowUp, MdKeyboardArrowDown } from "react-icons/md";
import { Formik } from "formik";
import * as Yup from "yup";
import { Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import NewTableComponent from "../../../../components/NewTableComponent";
import CustomModal from "../../../../components/CustomModal";
import ConfirmDeleteModal from "../../../../components/modals/ConfirmDeleteModal";
import Input from "../../../../components/input";
import Select from "../../../../components/Select";
import Button from "../../../../components/button";
import MultiLineText from "../../../../components/MultiLineText";
import { fetchCustomerDropdown } from "../../../../store/Slices/dropdownSlice";
import { apiService } from "../../../../services/apiService";
import { successNotify, errorNotify } from "../../../../utils/toast";
import { INVOICE_TYPES } from "../../../../utils/app-constant";

const InvoiceColumnsTab = ({ addTrigger }) => {
  const dispatch = useDispatch();
  const customerDropdownData = useSelector((state) => state.dropdowns?.customerDropdownData || []);

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [searchFilters, setSearchFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [columnMaster, setColumnMaster] = useState([]);
  const [apiRows, setApiRows] = useState([]);
  const [meta, setMeta] = useState({ totalItems: 0, totalPages: 1 });
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  // Form states
  const [showFormModal, setShowFormModal] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState({
    profile_id: "",
    name: "",
    customer: "",
    invoiceType: "",
    visibleColumns: [],
    columnLabels: {},
    showTaxLine: false,
    showProvincePair: false,
    showPaymentGuidelines: false,
    ibanNumber: "",
    status: "",
  });

  // Delete states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch customer dropdown data from Redux if empty
  useEffect(() => {
    if (!customerDropdownData || customerDropdownData.length === 0) {
      dispatch(fetchCustomerDropdown());
    }
  }, [dispatch, customerDropdownData]);

  // Fetch column master data from API on mount
  useEffect(() => {
    const fetchColumnMaster = async () => {
      try {
        const response = await apiService.getInvoiceColumnMaster();
        setColumnMaster(response?.data || []);
      } catch (error) {
        errorNotify(error || "Failed to fetch column master options");
      }
    };
    fetchColumnMaster();
  }, []);

  // Fetch paginated configurations
  useEffect(() => {
    let isMounted = true;

    const fetchConfigs = async () => {
      setLoading(true);
      try {
        const response = await apiService.getInvoiceColumnConfigsPaginated({
          page: currentPage,
          limit,
          search_text: searchText,
          searchFilters,
        });

        const payload = response?.data || {};
        const nextRows = Array.isArray(payload?.results || payload?.data)
          ? payload.results || payload.data
          : [];

        if (!isMounted) return;

        setApiRows(nextRows);
        setMeta({
          totalItems: Number(payload?.meta?.totalItems || payload?.total || nextRows.length),
          totalPages: Number(payload?.meta?.totalPages || 1),
        });
      } catch (error) {
        if (!isMounted) return;
        setApiRows([]);
        setMeta({ totalItems: 0, totalPages: 1 });
        errorNotify(error || "Failed to fetch column configuration profiles");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchConfigs();

    return () => {
      isMounted = false;
    };
  }, [currentPage, limit, searchText, searchFilters, refetchTrigger]);

  const getCustomerLabel = (val) => {
    if (val === 0 || val === "0" || !val) return "All Customers";
    const cust = customerDropdownData.find((c) => String(c.id || c.value) === String(val));
    return cust ? cust.name || cust.label : `Customer #${val}`;
  };

  const invoiceTypeOptions = [
    { value: INVOICE_TYPES.MONTHLY, label: "Monthly" },
    { value: INVOICE_TYPES.WEEKLY, label: "Weekly" },
    { value: INVOICE_TYPES.AD_HOC, label: "Ad-hoc" },
  ];

  const customerDropdownOptions = customerDropdownData.map((c) => ({
    value: String(c.id || c.value || ""),
    label: c.name || c.label || "",
  }));

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const PK_IBAN_REGEX = /^PK\d{2}[A-Z]{4}[A-Z0-9]{16}$/i;

  const columnValidationSchema = Yup.object().shape({
    name: Yup.string().required("Profile name is required"),
    customer: Yup.string().required("Customer selection is required"),
    invoiceType: Yup.string().required("Invoice type is required"),
    status: Yup.string().required("Status is required"),
    visibleColumns: Yup.array().min(1, "At least one visible column must be selected"),
    ibanNumber: Yup.string().test(
      "iban-validation",
      "Enter a valid Pakistani IBAN (e.g. PK00AAAA0000000000000000)",
      function (value) {
        const { showPaymentGuidelines } = this.parent;
        if (showPaymentGuidelines) {
          if (!value || !value.trim()) {
            return this.createError({ message: "IBAN Number is required when Guidelines are enabled" });
          }
          return PK_IBAN_REGEX.test(value.trim());
        }
        if (value && value.trim()) {
          return PK_IBAN_REGEX.test(value.trim());
        }
        return true;
      }
    ),
  });

  // React to add trigger from parent
  useEffect(() => {
    if (addTrigger > 0) {
      handleOpenAdd();
    }
  }, [addTrigger]);

  const handleOpenAdd = () => {
    setInitialFormValues({
      profile_id: "",
      name: "",
      customer: "",
      invoiceType: "",
      visibleColumns: [],
      columnLabels: {},
      showTaxLine: false,
      showProvincePair: false,
      showPaymentGuidelines: false,
      ibanNumber: "",
      status: "",
    });
    setShowFormModal(true);
  };

  const handleOpenEdit = async (row) => {
    try {
      setLoading(true);
      const res = await apiService.getInvoiceColumnConfigDetail(row.id);
      const detail = res?.data || {};

      const items = detail.items || [];
      const sortedItems = [...items].sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
      const visibleCols = sortedItems.filter(it => it.is_visible).map(it => it.column_key);
      const labels = {};
      items.forEach(it => {
        if (it.custom_name) {
          labels[it.column_key] = it.custom_name;
        }
      });

      setInitialFormValues({
        profile_id: detail.id || "",
        name: detail.template_name || detail.name || "",
        customer: detail.customer_id !== undefined ? String(detail.customer_id) : "",
        invoiceType: detail.invoice_type || "",
        visibleColumns: visibleCols,
        columnLabels: labels,
        showTaxLine: !!detail.show_tax_line,
        showProvincePair: !!detail.show_province_pair,
        showPaymentGuidelines: !!detail.show_payment_guidelines,
        ibanNumber: detail.iban_number || "",
        status: detail.status ? String(detail.status).toLowerCase() : "",
      });
      setShowFormModal(true);
    } catch (error) {
      errorNotify(error || "Failed to fetch configurations details");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Map columns from visibleColumns & columnLabels using master
      const itemsPayload = values.visibleColumns.map((key, index) => {
        return {
          column_key: key,
          custom_name: values.columnLabels[key] || "",
          sort_order: index + 1,
          is_visible: true,
        };
      });

      const payload = {
        customer_id: values.customer === "0" ? 0 : Number(values.customer),
        template_name: values.name,
        invoice_type: values.invoiceType,
        status: values.status,
        show_tax_line: values.showTaxLine,
        show_province_pair: values.showProvincePair,
        show_payment_guidelines: values.showPaymentGuidelines,
        iban_number: values.ibanNumber || "",
        items: itemsPayload,
      };

      if (values.profile_id) {
        await apiService.updateInvoiceColumnConfig({
          id: Number(values.profile_id),
          invoiceColumnConfiguration_id: Number(values.profile_id),
          ...payload,
        });
        successNotify("Column configuration updated successfully");
      } else {
        await apiService.createInvoiceColumnConfig(payload);
        successNotify("Column configuration created successfully");
      }

      setShowFormModal(false);
      setRefetchTrigger((prev) => prev + 1);
    } catch (error) {
      errorNotify(error || "Failed to save configuration profile");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenDelete = (id) => {
    setDeleteTargetId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return;
    setDeleteLoading(true);
    try {
      await apiService.deleteInvoiceColumnConfig(deleteTargetId);
      successNotify("Column configuration deleted successfully");
      setShowDeleteModal(false);
      setDeleteTargetId(null);
      setRefetchTrigger((prev) => prev + 1);
    } catch (error) {
      errorNotify(error || "Failed to delete column configuration");
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      name: "Configuration Name",
      key: "template_name",
      selector: (row) => row.template_name || row.name || "",
      cell: (row) => (
        <MultiLineText text={row.template_name || row.name || "-"} maxWidth="150px" />
      ),
      tableColumn: true,
      filterName: "nameFilter",
      type: "text",
      minWidth: "180px",
    },
    {
      name: "Customer",
      key: "customer_id",
      selector: (row) => getCustomerLabel(row.customer_id || row.customer),
      cell: (row) => (
        <MultiLineText text={getCustomerLabel(row.customer_id || row.customer)} maxWidth="130px" />
      ),
      tableColumn: true,
      filterName: "customerFilter",
      type: "text",
      minWidth: "160px",
    },
    {
      name: "Invoice Type",
      key: "invoice_type",
      selector: (row) => {
        const type = row.invoice_type || row.invoiceType;
        const found = invoiceTypeOptions.find((opt) => opt.value === type);
        return found ? found.label : type || "-";
      },
      tableColumn: true,
      filterName: "invoiceTypeFilter",
      type: "select",
      options: invoiceTypeOptions,
      minWidth: "140px",
    },
    {
      name: "Tax Line",
      key: "show_tax_line",
      selector: (row) => (row.show_tax_line || row.showTaxLine ? "Yes" : "No"),
      tableColumn: true,
      filterName: "showTaxLineFilter",
      type: "select",
      options: [
        { value: "true", label: "Yes" },
        { value: "false", label: "No" },
      ],
      minWidth: "120px",
    },
    {
      name: "Status",
      key: "status",
      selector: (row) => row.status,
      cell: (row) => (
        <span
          className={`settings_badge ${String(row.status).toLowerCase() === "active"
            ? "settings_badge_active"
            : "settings_badge_inactive"
            }`}
        >
          {row.status === "active" || row.status === "Active" ? "Active" : "Inactive"}
        </span>
      ),
      tableColumn: true,
      filterName: "statusFilter",
      type: "select",
      options: statusOptions,
      minWidth: "130px",
    },
    {
      name: "Action",
      key: "action",
      cell: (row) => (
        <div className="table_action_btn">
          <button
            type="button"
            className="edit_btn"
            title="Edit profile"
            onClick={() => handleOpenEdit(row)}
          >
            <LuPencil />
          </button>
          <button
            type="button"
            className="delete_btn"
            title="Delete profile"
            onClick={() => handleOpenDelete(row.id)}
          >
            <RiDeleteBinLine />
          </button>
        </div>
      ),
      tableColumn: false,
      minWidth: "100px",
      grow: 0,
    },
  ];

  return (
    <div className="mt-2">
      <NewTableComponent
        showAddBtn={false}
        columns={columns}
        data={apiRows}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        limit={limit}
        setLimit={setLimit}
        totalItems={meta.totalItems}
        totalPages={meta.totalPages}
        onSearch={setSearchText}
        searchValue={searchText}
        loading={loading}
        paginationLoading={loading}
        setSearchFilters={setSearchFilters}
        searchFilters={searchFilters}
        limitOptions={[10, 20, 50]}
        showFilter={false}
        showPagination={true}
      />

      <CustomModal
        show={showFormModal}
        handleClose={() => setShowFormModal(false)}
        heading={initialFormValues.profile_id ? "Edit Column Profile" : "Add Column Profile"}
        size="lg"
      >
        <Formik
          initialValues={initialFormValues}
          validationSchema={columnValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            isSubmitting,
            submitForm,
          }) => {
            const handleToggleCol = (col) => {
              let list = [...values.visibleColumns];
              const idx = list.indexOf(col);
              if (idx >= 0) {
                list.splice(idx, 1);
              } else {
                list.push(col);
              }
              setFieldValue("visibleColumns", list);
            };

            const handleMoveCol = (col, direction) => {
              let list = [...values.visibleColumns];
              const idx = list.indexOf(col);
              const targetIdx = idx + direction;
              if (targetIdx < 0 || targetIdx >= list.length) return;

              const temp = list[idx];
              list[idx] = list[targetIdx];
              list[targetIdx] = temp;
              setFieldValue("visibleColumns", list);
            };

            const handleCustomLabelChange = (col, val) => {
              let labels = { ...values.columnLabels };
              if (!val) {
                delete labels[col];
              } else {
                labels[col] = val;
              }
              setFieldValue("columnLabels", labels);
            };

            return (
              <div>
                <Row>
                  <Col md={6}>
                    <Input
                      label="Profile Name"
                      placeholder="e.g. Standard Layout"
                      type="text"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.name}
                      touched={touched.name}
                      required
                    />
                  </Col>
                  <Col md={6}>
                    <Select
                      label="Customer"
                      placeholder="Select Customer"
                      options={customerDropdownOptions}
                      value={
                        customerDropdownOptions.find(
                          (opt) => opt.value === values.customer
                        ) || null
                      }
                      name="customer"
                      onChange={(option) =>
                        setFieldValue("customer", option ? option.value : "")
                      }
                      onBlur={handleBlur}
                      error={errors.customer}
                      touched={touched.customer}
                      required
                    />
                  </Col>

                  <Col md={6}>
                    <Select
                      label="Invoice Type"
                      placeholder="Select Invoice Type"
                      options={invoiceTypeOptions}
                      value={
                        invoiceTypeOptions.find(
                          (opt) => opt.value === values.invoiceType
                        ) || null
                      }
                      name="invoiceType"
                      onChange={(option) =>
                        setFieldValue(
                          "invoiceType",
                          option ? option.value : ""
                        )
                      }
                      onBlur={handleBlur}
                      error={errors.invoiceType}
                      touched={touched.invoiceType}
                      required
                    />
                  </Col>
                  <Col md={6}>
                    <Select
                      label="Status"
                      placeholder="Select Status"
                      options={statusOptions}
                      value={
                        statusOptions.find((opt) => opt.value === values.status) ||
                        null
                      }
                      name="status"
                      onChange={(option) =>
                        setFieldValue("status", option ? option.value : "")
                      }
                      onBlur={handleBlur}
                      error={errors.status}
                      touched={touched.status}
                      required
                    />
                  </Col>

                  <Col md={12} className="mt-3">
                    <div className="settings_form_group">
                      <label className="d-block font-bold mb-2">
                        Select Visible Columns (Click to toggle) <span className="text-danger">*</span>
                      </label>

                      <div className="token_checklist">
                        {columnMaster.map((col) => {
                          const key = col.column_key || col.slug;
                          const name = col.default_name || col.name || key;
                          const isActive = values.visibleColumns.includes(key);
                          return (
                            <button
                              key={key}
                              type="button"
                              className={`token_pill ${isActive ? "active" : "inactive"}`}
                              onClick={() => handleToggleCol(key)}
                            >
                              {name}
                            </button>
                          );
                        })}
                      </div>
                      {errors.visibleColumns && touched.visibleColumns && (
                        <div className="text-danger mb-2 text-xs">{errors.visibleColumns}</div>
                      )}
                    </div>
                  </Col>

                  <Col md={12} className="mt-3">
                    <div className="settings_form_group">
                      <label className="d-block font-bold mb-2">Column Order and Custom Labels</label>
                      <div className="column_order_list">
                        {values.visibleColumns.map((col, idx) => {
                          const masterCol = columnMaster.find(
                            (c) => (c.column_key || c.slug) === col
                          );
                          const defaultName = masterCol
                            ? masterCol.default_name || masterCol.name
                            : col;
                          return (
                            <div key={col} className="column_order_item">
                              <div className="d-flex align-items-center gap-2">
                                <span style={{ color: "#888", width: "20px" }}>
                                  {idx + 1}.
                                </span>
                                <strong>{defaultName}</strong>
                              </div>
                              <div className="d-flex align-items-center gap-3">
                                <input
                                  type="text"
                                  style={{
                                    width: "180px",
                                    padding: "4px 8px",
                                    fontSize: "12px",
                                  }}
                                  value={values.columnLabels[col] || ""}
                                  placeholder="Custom Label"
                                  onChange={(e) =>
                                    handleCustomLabelChange(col, e.target.value)
                                  }
                                />
                                <div className="column_order_actions">
                                  <button
                                    type="button"
                                    className="column_order_btn"
                                    disabled={idx === 0}
                                    onClick={() => handleMoveCol(col, -1)}
                                  >
                                    <MdKeyboardArrowUp />
                                  </button>
                                  <button
                                    type="button"
                                    className="column_order_btn"
                                    disabled={
                                      idx === values.visibleColumns.length - 1
                                    }
                                    onClick={() => handleMoveCol(col, 1)}
                                  >
                                    <MdKeyboardArrowDown />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </Col>

                  <Col md={6} className="d-flex align-items-center gap-2 mb-3">
                    <input
                      type="checkbox"
                      id="showTaxLine"
                      checked={values.showTaxLine}
                      onChange={(e) => setFieldValue("showTaxLine", e.target.checked)}
                      style={{ width: "auto", cursor: "pointer" }}
                    />
                    <label htmlFor="showTaxLine" className="m-0 cursor-pointer text-xs">
                      Show Tax Line
                    </label>
                  </Col>
                  <Col md={6} className="d-flex align-items-center gap-2 mb-3">
                    <input
                      type="checkbox"
                      id="showProvincePair"
                      checked={values.showProvincePair}
                      onChange={(e) => setFieldValue("showProvincePair", e.target.checked)}
                      style={{ width: "auto", cursor: "pointer" }}
                    />
                    <label htmlFor="showProvincePair" className="m-0 cursor-pointer text-xs">
                      Show Province Pair
                    </label>
                  </Col>

                  <Col md={12} className="mt-3">
                    <div className="bg-light p-3 rounded">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          id="showPaymentGuidelines"
                          checked={values.showPaymentGuidelines}
                          onChange={(e) => setFieldValue("showPaymentGuidelines", e.target.checked)}
                          style={{ width: "auto", cursor: "pointer" }}
                        />
                        <label htmlFor="showPaymentGuidelines" className="m-0 cursor-pointer font-bold">
                          Enable Payment Guidelines Footer
                        </label>
                      </div>
                      <div className="settings_form_group">
                        <Input
                          label="IBAN Number"
                          placeholder="PK00AAAA0000000000000000"
                          type="text"
                          name="ibanNumber"
                          disabled={!values.showPaymentGuidelines}
                          value={values.ibanNumber || ""}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\s/g, "").toUpperCase().slice(0, 24);
                            setFieldValue("ibanNumber", val);
                          }}
                          onBlur={handleBlur}
                          error={errors.ibanNumber}
                          touched={touched.ibanNumber}
                          required={values.showPaymentGuidelines}
                        />
                        <small className="text-muted">
                          Guideline: "Payment should be made in favor of J HOLDINGS (PRIVATE) LIMITED..."
                        </small>
                      </div>
                    </div>
                  </Col>

                  <Col md={12}>
                    <div className="d-flex gap-2 mt-3">
                      <Button
                        cancelBtn={() => setShowFormModal(false)}
                        submitBtn={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          submitForm();
                        }}
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        submitText="Save"
                        type="button"
                        shouldRow={false}
                        buttonExtraCss="fuel_add_btn"
                      />
                    </div>
                  </Col>
                </Row>
              </div>
            );
          }}
        </Formik>
      </CustomModal>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
        title="Delete Column Configuration Profile"
        description="Are you sure you want to delete this configuration profile? This action cannot be undone."
      />
    </div>
  );
};

export default InvoiceColumnsTab;
